import {Color} from '@benchling/nucleus-ui';
import {packSx, Stack, useMantineTheme, type StackProps} from '@mantine/core';
import React, {PropsWithChildren} from 'react';

import {BenchlingNodeStatus} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeConstants';
import {useBenchlingNodeContext} from 'ui-library/reactflow/nodes/BenchlingNode/useBenchlingNodeContext';

const NODE_HEADER_COLOR_NAMES: Record<BenchlingNodeStatus, string> = {
  error: 'red',
  'in-progress': 'teal',
  pending: 'gray',
  success: 'green',
  warning: 'orange',
  'ai-proposed': 'purple',
  halted: 'gray',
};

/**
 * Context-consumer hook to get the background color of a flowchart node header.
 */
const useBenchlingNodeHeaderColor: () => string = () => {
  const {status, isDisabled} = useBenchlingNodeContext();
  const theme = useMantineTheme();
  // Special case: if the node is disabled, the header should be gray
  if (isDisabled) {
    return theme.colors.gray[2];
  }
  // Special case: if we don't have a status, default to white
  if (status == null) {
    return theme.white;
  }
  const colorName = NODE_HEADER_COLOR_NAMES[status];
  switch (status) {
    // Special case: pending and halted are one shade darker than the rest of these
    case 'pending':
    case 'halted':
      return theme.colors[colorName][1];
    case 'error':
    case 'in-progress':
    case 'success':
    case 'warning':
    case 'ai-proposed':
      return theme.colors[colorName][0];
    default:
      return theme.white;
  }
};

/**
 * A lightweight header for a {@link BenchlingNode}. This is a flex column with BenchlingNode styling
 * applied.
 * It consumes the context provided by {@link BenchlingNode}, and as such automatically handles node status
 * and disabled status.
 * @package
 */
export function Header(props: PropsWithChildren<StackProps>): JSX.Element {
  const backgroundColor = useBenchlingNodeHeaderColor();
  const {sx, ...stackProps} = props;
  return (
    <Stack
      bg={backgroundColor}
      spacing="xs"
      p="sm"
      pos="relative"
      sx={[
        (theme) => ({
          borderTopLeftRadius: theme.radius.md,
          borderTopRightRadius: theme.radius.md,
          borderBottom: `1px solid ${Color.GRAY_300}`,
          '&:last-child': {
            borderBottomWidth: 0,
            borderBottomLeftRadius: theme.radius.md,
            borderBottomRightRadius: theme.radius.md,
          },
        }),
        ...packSx(sx),
      ]}
      {...stackProps}
    />
  );
}
